import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import { FC, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Employer } from '../types';
import Delete from '@mui/icons-material/Delete';
import LoginIcon from '@mui/icons-material/Login';
import { useGetEmployerMatrics } from '../hooks/useGetEmployerMatrics';
import EditIcon from '@mui/icons-material/Edit';
import LanguageIcon from '@mui/icons-material/Language';
import LockResetIcon from '@mui/icons-material/LockReset';
import AddIcon from '@mui/icons-material/Add';
import WarningIcon from '@mui/icons-material/Warning';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EmployerDeleteModal from './EmployerDeleteModal';
import EmployerEditUrl from './EmployerEditUrl';
import ExtendTrial from './ExtendTrial';
import { useGetEmployerTrialCount } from '../hooks/useGetEmployerTrialCount';
import { white } from '@styles/colors';
import CancelServiceModal from './CancelServiceModal';
import EmployerResetPasswordModal from './EmployerResetPasswordModal';
import { useLoginUser } from '../hooks/useLoginAsUser';
import PickImageModal from '@components/PickImageModal/PickImageModal';
import GraphicRequestedModal from '@features/boostMyJobs/components/GraphicRequestedModal/GraphicRequestedModal';
import AdminCustomGraphicsModal from './AdminCustomGraphicsModal';
import AdminPostHiringLinkModal from './AdminPostHiringLinkModal';
import { useAdminUpdateProfileImage } from '../hooks/useAdminUpdateProfileImage';
import { useGetEmployerPaidCount } from '../hooks/useGetEmployerPaidCount';
import CallMissedOutgoingIcon from '@mui/icons-material/CallMissedOutgoing';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { useApproveSpamEmployer } from '../hooks/useApproveSpamEmployer';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import BlockIcon from '@mui/icons-material/Block';
import { useBlockSpamEmployer } from '../hooks/useBlockSpamEmployer';
import { useApproveBlockEmployer } from '../hooks/useApproveBlockEmployer';
import AccountBasicInfo from './AccountBasicInfo';
import AccountApplicationInfo from './AccountApplicationInfo';
import AccountDetailedInfo from './AccountDetailedInfo';

type EmployerMetricsModalProps = {
    item: Employer;
    handleClose: () => void;
    spamAccount?: Boolean
    blockAccount?: Boolean
};

const EmployerMetricsModal: FC<EmployerMetricsModalProps> = ({
    item,
    spamAccount,
    blockAccount,
    handleClose,
}) => {
    const companyUrl = `${import.meta.env.VITE_MARKETING_SITE_URL}${item?.company ? item?.company?.s_slug : item?.enterprise?.s_slug}`;
    const [alertText, setAlertText] = useState<string>('');
    const [alertPaidText, setAlertPaidText] = useState<string>('');
    const { companyData, isLoadingCompanyData } = useGetEmployerMatrics({
        setAlertText,
        company_id: item.company && item.company.id,
        enterprise_id: item.enterprise && item.enterprise.id,
    });
    const { trialCount } = useGetEmployerTrialCount({
        setAlertText,
        id: item.company && item.company.plan == 999 && item.company.id,
    });
    const { paidCount, isLoadingPaidCount } = useGetEmployerPaidCount({
        setAlertText: setAlertPaidText,
        id: (item.company && (item.company.plan == 1 || item.company.plan == 2 || item.company.plan == 3 || item.company.plan == 4)) ?
            item.company.id : item.enterprise && item.enterprise?.id ,
        isEnterprise: item.enterprise ? true : false,
    });
    const { userLogin } = useLoginUser();
    const { approveSpamEmployer, isApproveSpamEmployerLoading } = useApproveSpamEmployer({ setAlertText });
    const { approveBlockEmployer, isApproveBlockEmployerLoading } = useApproveBlockEmployer({ setAlertText });
    const { blockSpamEmployer, isBlockSpamEmployerLoading } = useBlockSpamEmployer({ setAlertText });
    const [isEditUrl, setIsEditUrl] = useState<boolean>(false);
    const [isResetPassword, setIsResetPassword] = useState<boolean>(false);
    const [isExtendTrial, setIsExtendTrial] = useState<boolean>(false);
    const [isCancelService, setIsCancelService] = useState<boolean>(false);
    const [isDeleteUser, setIsDeleteUser] = useState<boolean>(false);
    const [isPostLinkModalOpen, setIsPostLinkModalOpen] = useState(false);
    const [isGraphicModalOpen, setIsGraphicModalOpen] = useState(false);
    const [isSelectImageModalOpen, setIsSelectImageModalOpen] = useState(false);
    const [isGraphicRequestedModalOpen, setIsGraphicRequestedModalOpen] =
        useState(false);

    const { updateProfileImage, isUpdatingProfileImage } =
        useAdminUpdateProfileImage({
            setAlertText,
        });

    const handleLogin = () => {
        userLogin({ user_id: item.id });
    };

    const handleApproveSpamAccount = () => {
        const options = {
            onSuccess: (data: { message: string, status: number }) => {
                if (data.status === 200) {
                    toast.success(data.message);
                    handleClose();
                }
            },
        };
        if (spamAccount) {
            approveSpamEmployer({ company_id: item.company?.id }, options);
        } else if (blockAccount) {
            approveBlockEmployer({ company_id: item.company?.id }, options);
        }
    };

    const handleBlockSpamAccount = () => {
        const options = {
            onSuccess: (data: { message: string, status: number }) => {
                if (data.status === 200) {
                    toast.success(data.message);
                    handleClose();
                }
            },
        };
        blockSpamEmployer({ company_id: item.company?.id }, options);
    };

    const showPostHiringLinkModal = () => {
        const hasCompanySocialImage = item.enterprise
            ? item.has_social_image_enterprise
            : item.has_social_image;

        if (hasCompanySocialImage) {
            setIsPostLinkModalOpen(true);
        } else {
            setIsGraphicModalOpen(true);
        }
    };

    return (
        <Dialog
            open
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            disableEnforceFocus
        >
            <DialogTitle>
                <IconButton
                    sx={{ position: 'absolute', top: 5, right: 5 }}
                    onClick={handleClose}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {alertText && (
                    <Alert sx={{ margin: '10px 0' }} severity="error">
                        {alertText}
                    </Alert>
                )}
                <AccountBasicInfo
                    account={item}
                    trialCount={trialCount}
                    paidCount={paidCount}
                    isLoadingPaidCount={isLoadingPaidCount}
                    alertPaidText={alertPaidText}
                />
                <Stack
                    direction="row"
                    spacing={5}
                    marginTop={2}
                    justifyContent="space-between"
                >
                    {companyData && companyData?.object?.current ? (
                        <AccountApplicationInfo
                           account={companyData?.object?.current}
                        />
                    ) : (
                        isLoadingCompanyData && (
                            <Stack alignItems="center">
                                <CircularProgress />
                            </Stack>
                        )
                    )}
                    <AccountDetailedInfo
                        account={item}
                        paidCount={paidCount}
                    />
                    <Stack sx={{ marginTop: '35px !important' }} spacing={2}>
                        {item.enabled ? (
                            <>
                                <Button
                                    onClick={handleLogin}
                                    variant="contained"
                                    sx={{ width: '100%', fontSize: '14px' }}
                                    color="success"
                                    startIcon={<LoginIcon />}
                                >
                                    Sign in as user
                                </Button>
                                <Button
                                    variant="contained"
                                    sx={{ width: '100%', fontSize: '14px' }}
                                    color="warning"
                                    startIcon={<CallMissedOutgoingIcon />}
                                    component="a"
                                    href={companyUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                   View jobs page
                                </Button>
                                {spamAccount || blockAccount ? (
                                    <Button
                                        onClick={() => {
                                            const url = `${import.meta.env.VITE_MARKETING_SITE_URL}${item.company.s_slug}`;
                                            window.open(url, '_blank'); // Open the URL in a new tab
                                        }}
                                        variant="contained"
                                        sx={{ width: '100%', fontSize: '14px' }}
                                        color="primary"
                                        startIcon={<CallMissedOutgoingIcon />}
                                    >
                                        Go To Hiring Page
                                    </Button>
                                ) : null}
                                {!spamAccount && !blockAccount ? (
                                    <Button
                                        onClick={() => setIsEditUrl(true)}
                                        variant="contained"
                                        sx={{ width: '100%', fontSize: '14px' }}
                                        color="primary"
                                        startIcon={<EditIcon />}
                                    >
                                        Edit Url
                                    </Button>
                                ) : null}
                                {(item.company && !spamAccount && !blockAccount &&
                                    (item.company.plan === 1 || item.company.plan === 2 || item.company.plan === 4) &&
                                    paidCount?.object !== undefined) || item.enterprise ? (
                                    <>
                                        {item.company && (
                                            <Button
                                                onClick={showPostHiringLinkModal}
                                                variant="contained"
                                                sx={{ width: '100%', fontSize: '14px' }}
                                                color="primary"
                                                startIcon={<LanguageIcon />}
                                            >
                                                Post to social
                                            </Button>
                                        )}
                                        <Button
                                            onClick={() => setIsResetPassword(true)}
                                            variant="contained"
                                            sx={{ width: '100%', fontSize: '14px' }}
                                            color="primary"
                                            startIcon={<LockResetIcon />}
                                        >
                                            Reset Password
                                        </Button>
                                        
                                    </>
                                ) : null}

                                {item.company && !spamAccount && !blockAccount && (item.company.plan === 999 || item.company.plan === 0) ? (
                                    <Button
                                        onClick={() => setIsExtendTrial(true)}
                                        variant="contained"
                                        sx={{ width: '100%', fontSize: '14px' }}
                                        color="primary"
                                        startIcon={<AccessTimeIcon />}
                                    >
                                        {item.company.plan === 999 ? 'Extend Trial' : 'Create  Trial'}
                                    </Button>
                                ) : null}
                                {((item.company && item?.master_account &&
                                    (item.company.plan === 1 || item.company.plan === 2 || item.company.plan === 4)) || item.enterprise) && !spamAccount && !blockAccount  &&
                                    paidCount?.object ? (
                                    <Button
                                        onClick={() => setIsCancelService(true)}
                                        variant="contained"
                                        sx={{ width: '100%', fontSize: '14px' }}
                                        color="error"
                                        startIcon={<WarningIcon />}
                                    >
                                        Cancel Service
                                    </Button>
                                ) : null}
                                {spamAccount ? (
                                    <LoadingButton
                                        loading={isBlockSpamEmployerLoading}
                                        onClick={handleBlockSpamAccount}
                                        variant="contained"
                                        sx={{ width: '100%', fontSize: '14px' }}
                                        color="primary"
                                        startIcon={<BlockIcon />}
                                    >
                                        Block
                                    </LoadingButton>
                                ) : null}
                                {spamAccount || blockAccount ? (
                                    <LoadingButton
                                        loading={isApproveSpamEmployerLoading}
                                        onClick={handleApproveSpamAccount}
                                        variant="contained"
                                        sx={{ width: '100%', fontSize: '14px' }}
                                        color="primary"
                                        startIcon={<VerifiedUserIcon />}
                                    >
                                        Approve
                                    </LoadingButton>
                                ) : null}
                                {((item?.company?.plan === 999) || (item?.company?.plan === 0) || (paidCount?.status == 404)) && (
                                    <Button
                                        onClick={() => setIsDeleteUser(true)}
                                        variant="contained"
                                        sx={{ width: '100%', fontSize: '14px' }}
                                        color="error"
                                        startIcon={<Delete />}
                                    >
                                        Delete User
                                    </Button>
                                )}
                                {((item?.enterprise?.plan === 999) || (item?.enterprise?.plan === 0)) && (
                                    <Button
                                        onClick={() => setIsDeleteUser(true)}
                                        variant="contained"
                                        sx={{ width: '100%', fontSize: '14px' }}
                                        color="error"
                                        startIcon={<Delete />}
                                    >
                                        Delete User
                                    </Button>
                                )}
                            </>
                        ) :
                        <>
                        <Typography
                            sx={{
                                width: '150px',
                                textAlign: 'center',
                                color: white,
                                background: '#a21c1d',
                                borderRadius: 5,
                            }}
                        >
                            User Deactivated
                        </Typography>
                        <Button
                            onClick={() => setIsDeleteUser(true)}
                            variant="contained"
                            sx={{ width: '100%', fontSize: '14px' }}
                            color="error"
                            startIcon={<Delete />}
                        >
                            Delete User
                        </Button>
                        </> 
                        }
                    </Stack>
                </Stack>
                {isDeleteUser && (
                    <EmployerDeleteModal
                        handleClose={() => setIsDeleteUser(false)}
                        item={item}
                    />
                )}
                {isEditUrl && (
                    <EmployerEditUrl
                        handleClose={() => setIsEditUrl(false)}
                        item={item}
                    />
                )}
                {isExtendTrial && (
                    <ExtendTrial
                        handleClose={() => setIsExtendTrial(false)}
                        item={item}
                        paidCheck={item?.company?.plan === 0 ? true : false}
                    />
                )}
                {isCancelService && (
                    <CancelServiceModal
                        handleClose={() => setIsCancelService(false)}
                        item={item}
                    />
                )}
                {isResetPassword && (
                    <EmployerResetPasswordModal
                        handleClose={() => setIsResetPassword(false)}
                        item={item}
                    />
                )}
                <AdminCustomGraphicsModal
                    isOpen={isGraphicModalOpen}
                    handleClose={() => setIsGraphicModalOpen(false)}
                    handleRequestGraphicSuccess={() => {
                        setIsGraphicModalOpen(false);
                        setIsGraphicRequestedModalOpen(true);
                    }}
                    handleUploadImage={() => {
                        setIsGraphicModalOpen(false);
                        setIsSelectImageModalOpen(true);
                    }}
                    handleContinueWithout={() => {
                        setIsGraphicModalOpen(false);
                        setIsPostLinkModalOpen(true);
                    }}
                    item={item}
                />
                <GraphicRequestedModal
                    isOpen={isGraphicRequestedModalOpen}
                    handleClose={() => setIsGraphicRequestedModalOpen(false)}
                />
                <AdminPostHiringLinkModal
                    isOpen={isPostLinkModalOpen}
                    handleClose={() => setIsPostLinkModalOpen(false)}
                    item={item}
                />
                <PickImageModal
                    isOpen={isSelectImageModalOpen}
                    setIsOpen={setIsSelectImageModalOpen}
                    title="Upload Image"
                    imageType="social_image"
                    minWidth={0}
                    minHeight={0}
                    alertText={alertText}
                    setAlertText={setAlertText}
                    handleUpdateImage={updateProfileImage}
                    isUpdatingImage={isUpdatingProfileImage}
                    item={item}
                />
            </DialogContent>
        </Dialog>
    );
};

export default EmployerMetricsModal;
